import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@yk/ui";
import { apiFetch } from "@yk/frontend-core";
import type {
  ComplaintRow,
  ComplaintDetail,
  ListResponse,
  DetailResponse,
  CategoryRow,
  TimelineEntry,
} from "./types.js";

export interface ComplaintListParams {
  status?: string;
  priority?: string;
  /** Free-text search across work-order number + title. */
  search?: string;
  // FixFlow Phase-4 SLICE D — essential complaint-list filters.
  departmentId?: string;
  categoryId?: string;
  propertyId?: string;
  technicianUserId?: string;
  urgent?: boolean;
  escalated?: boolean;
  overdue?: boolean;
  /** ISO date (yyyy-mm-dd) — inclusive lower bound on createdAt. */
  dateFrom?: string;
  /** ISO date (yyyy-mm-dd) — inclusive upper bound on createdAt. */
  dateTo?: string;
  page?: number;
  limit?: number;
}

/** Pure mapping from filter params → the work-order list query string.
 *  Only set params produce keys; booleans emit only when true (absent = no
 *  filter). Exported for unit-testing the filter-to-query contract. */
export function buildComplaintQuery(params: ComplaintListParams): string {
  const qs = new URLSearchParams();
  if (params.search) qs.set("search", params.search);
  if (params.status) qs.set("status", params.status);
  if (params.priority) qs.set("priority", params.priority);
  if (params.departmentId) qs.set("departmentId", params.departmentId);
  if (params.categoryId) qs.set("categoryId", params.categoryId);
  if (params.propertyId) qs.set("propertyId", params.propertyId);
  if (params.technicianUserId)
    qs.set("technicianUserId", params.technicianUserId);
  if (params.urgent) qs.set("urgent", "true");
  if (params.escalated) qs.set("escalated", "true");
  if (params.overdue) qs.set("overdue", "true");
  if (params.dateFrom) qs.set("dateFrom", params.dateFrom);
  if (params.dateTo) qs.set("dateTo", params.dateTo);
  qs.set("page", String(params.page ?? 1));
  qs.set("limit", String(params.limit ?? 25));
  return qs.toString();
}

export function useComplaints(params: ComplaintListParams) {
  return useQuery({
    queryKey: ["complaints", params],
    queryFn: async () =>
      (await apiFetch(
        `/api/v1/co/maintenance/work-orders?${buildComplaintQuery(params)}`,
      )) as ListResponse<ComplaintRow>,
  });
}

export function useComplaint(id: string) {
  return useQuery({
    queryKey: ["complaint", id],
    queryFn: async () =>
      (await apiFetch(
        `/api/v1/co/maintenance/work-orders/${id}`,
      )) as DetailResponse<ComplaintDetail>,
    enabled: Boolean(id),
  });
}

/** Assembled, ordered audit + escalation timeline for a complaint (T1.4). */
export function useComplaintTimeline(id: string) {
  return useQuery({
    queryKey: ["complaint", id, "timeline"],
    queryFn: async () =>
      (await apiFetch(
        `/api/v1/co/maintenance/work-orders/${id}/timeline`,
      )) as DetailResponse<TimelineEntry[]>,
    enabled: Boolean(id),
  });
}

function useAssessmentAction<TBody extends Record<string, unknown>>(
  id: string,
  action: "approve" | "reject" | "escalate",
  successMessage: string,
) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (body: TBody) =>
      apiFetch(`/api/v1/work-orders/${id}/assessment/${action}`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      toast.success(successMessage);
      void qc.invalidateQueries({ queryKey: ["complaint", id] });
      void qc.invalidateQueries({ queryKey: ["complaints"] });
    },
    onError: (err) => toast.error(err),
  });
}

export const useApproveAssessment = (id: string) =>
  useAssessmentAction<{ supervisorNote?: string; approvalLevel?: number }>(
    id,
    "approve",
    "Assessment approved",
  );

export const useRejectAssessment = (id: string) =>
  useAssessmentAction<{ note: string }>(
    id,
    "reject",
    "Assessment rejected — sent back for rework",
  );

export const useEscalateAssessment = (id: string) =>
  useAssessmentAction<{ reason: string; multiDept?: boolean }>(
    id,
    "escalate",
    "Complaint escalated",
  );

export function useAssignTechnician(id: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (body: { technicianUserId: string }) =>
      apiFetch(`/api/v1/co/maintenance/work-orders/${id}/dispatch`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      toast.success("Technician assigned");
      void qc.invalidateQueries({ queryKey: ["complaint", id] });
      void qc.invalidateQueries({ queryKey: ["complaints"] });
    },
    onError: (err) => toast.error(err),
  });
}

export function useCategories() {
  return useQuery({
    queryKey: ["categories"],
    queryFn: async () =>
      (await apiFetch("/api/v1/co/maintenance/categories")) as {
        data: CategoryRow[];
      },
  });
}

export function useTriageComplaint(id: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (body: {
      categoryId: string;
      priority: string;
      note?: string;
    }) =>
      apiFetch(`/api/v1/co/maintenance/work-orders/${id}/triage`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      toast.success("Complaint triaged");
      void qc.invalidateQueries({ queryKey: ["complaint", id] });
      void qc.invalidateQueries({ queryKey: ["complaints"] });
    },
    onError: (err) => toast.error(err),
  });
}

// ── FixFlow Phase 5 — workflow actions ──────────────────────────────────────

/** Non-terminal statuses a complaint can be rescheduled from (mirrors the
 *  backend `work_order_not_reschedulable` gate). Exported for unit testing. */
export const RESCHEDULABLE_STATUSES: ReadonlySet<string> = new Set([
  "NEW",
  "TRIAGED",
  "DISPATCHED",
  "ACCEPTED",
  "IN_PROGRESS",
  "AWAITING_QUOTE",
  "AWAITING_PARTS",
  "AWAITING_TENANT",
  "REOPENED",
  "ESCALATED",
  "MULTI_DEPT_IN_PROGRESS",
  "RESCHEDULED",
]);

/** Pure validation for the reschedule date — mirrors the backend
 *  `reschedule_date_invalid` rule (must parse and be strictly in the future).
 *  Exported for unit testing. */
export function isValidRescheduleDate(
  value: string,
  now: Date = new Date(),
): boolean {
  if (!value) return false;
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) return false;
  return ts > now.getTime();
}

export function useMergeComplaint(id: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (body: { targetWorkOrderId: string }) =>
      apiFetch(`/api/v1/co/maintenance/work-orders/${id}/merge`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      toast.success("Complaint merged");
      void qc.invalidateQueries({ queryKey: ["complaint", id] });
      void qc.invalidateQueries({ queryKey: ["complaints"] });
    },
    onError: (err) => toast.error(err),
  });
}

export function useRescheduleComplaint(id: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (body: { scheduledAt: string; reason?: string }) =>
      apiFetch(`/api/v1/co/maintenance/work-orders/${id}/reschedule`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      toast.success("Complaint rescheduled");
      void qc.invalidateQueries({ queryKey: ["complaint", id] });
      void qc.invalidateQueries({ queryKey: ["complaints"] });
    },
    onError: (err) => toast.error(err),
  });
}

export function useDeclineComplaint(id: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (body: { reason: string }) =>
      apiFetch(`/api/v1/co/maintenance/work-orders/${id}/decline`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      toast.success("Complaint declined");
      void qc.invalidateQueries({ queryKey: ["complaint", id] });
      void qc.invalidateQueries({ queryKey: ["complaints"] });
    },
    onError: (err) => toast.error(err),
  });
}

export function useRecategorizeComplaint(id: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (body: { categoryId: string }) =>
      apiFetch(`/api/v1/co/maintenance/work-orders/${id}/recategorize`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      toast.success("Complaint recategorized");
      void qc.invalidateQueries({ queryKey: ["complaint", id] });
      void qc.invalidateQueries({ queryKey: ["complaints"] });
    },
    onError: (err) => toast.error(err),
  });
}

export function useSetUrgent(id: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (body: { urgent: boolean; reason?: string }) =>
      apiFetch(`/api/v1/co/maintenance/work-orders/${id}/urgent`, {
        method: "POST",
        body,
      }),
    onSuccess: (_data, vars) => {
      toast.success(vars.urgent ? "Marked urgent" : "Urgent flag cleared");
      void qc.invalidateQueries({ queryKey: ["complaint", id] });
      void qc.invalidateQueries({ queryKey: ["complaints"] });
    },
    onError: (err) => toast.error(err),
  });
}

export interface CreateComplaintBody {
  propertyId: string;
  unitId?: string;
  raisedByTenantId: string;
  title: string;
  description: string;
}

export function useCreateComplaint() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (body: CreateComplaintBody) =>
      (await apiFetch("/api/v1/co/maintenance/work-orders", {
        method: "POST",
        body,
      })) as {
        data: { id: string; number: string };
      },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["complaints"] }),
    onError: (err) => toast.error(err),
  });
}

export interface IntakeSteps {
  create: (b: CreateComplaintBody) => Promise<{ data: { id: string } }>;
  triage: (
    id: string,
    b: { categoryId: string; priority: string },
  ) => Promise<unknown>;
  assign: (id: string, b: { technicianUserId: string }) => Promise<unknown>;
}

/** Runs create → triage → (optional) assign, stopping at the first failure.
 *  Returns the reached stage so the UI can report partial progress. */
export async function runIntake(
  steps: IntakeSteps,
  input: {
    create: CreateComplaintBody;
    triage: { categoryId: string; priority: string };
    technicianUserId?: string;
  },
): Promise<{ id: string; reached: "triaged" | "assigned" }> {
  const { data } = await steps.create(input.create);
  await steps.triage(data.id, input.triage);
  if (input.technicianUserId) {
    await steps.assign(data.id, { technicianUserId: input.technicianUserId });
    return { id: data.id, reached: "assigned" };
  }
  return { id: data.id, reached: "triaged" };
}
